import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Sparkles, Zap, DollarSign, CheckCircle2, X, Star } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { playPreferredSound } from "@/lib/sounds";

const POLL_MS = 8000;

// Vibración opcional en móviles (patrón de notificación)
function vibrateIfSupported(kind = "normal") {
  if (typeof navigator === "undefined" || !navigator.vibrate) return;
  const pattern = kind === "high" || kind === "celebration" ? [200, 100, 200] : [150];
  try { navigator.vibrate(pattern); } catch (_) { /* noop */ }
}

// Alerta unificada: reproduce el sonido preferido del usuario + vibra
function playNotificationAlert(severity = "normal") {
  playPreferredSound();
  vibrateIfSupported(severity);
}

const SEVERITY_STYLES = {
  celebration: "border-amber-400/60 bg-amber-400/10",
  high: "border-[hsl(var(--primary))]/60 bg-[hsl(var(--primary))]/8",
  normal: "border-[hsl(var(--border))]",
};

const TYPE_ICON = {
  counter_offer: <Zap size={14} className="text-amber-300"/>,
  bid_received: <Sparkles size={14} className="text-sky-300"/>,
  bid_accepted: <CheckCircle2 size={14} className="text-emerald-300"/>,
  bid_rejected: <X size={14} className="text-muted-foreground"/>,
  delegation_paid: <DollarSign size={14} className="text-emerald-300"/>,
  mediation_paid: <DollarSign size={14} className="text-emerald-300"/>,
  escrow_released: <Star size={14} className="text-amber-300"/>,
  mediation_completed: <Star size={14} className="text-amber-300"/>,
  review_received: <Star size={14} className="text-amber-300"/>,
  admin_approved: <CheckCircle2 size={14} className="text-emerald-300"/>,
};

export default function NotificationsBell() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const lastSeenIdsRef = useRef(new Set());
  const dropdownRef = useRef(null);
  const firstLoadRef = useRef(true);

  const fetchNow = async () => {
    if (typeof document !== "undefined" && document.hidden) return;
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items);
      setUnread(data.unread_count);

      // Detect NEW notifications since last poll
      if (!firstLoadRef.current) {
        for (const n of data.items) {
          if (!lastSeenIdsRef.current.has(n.id) && !n.read) {
            // Toast + sound for fresh ones
            const className = n.severity === "celebration"
              ? "border-amber-400/60 bg-amber-950/70"
              : n.severity === "high"
              ? "border-[hsl(var(--primary))]/60 bg-[hsl(var(--background))]"
              : "";
            toast(n.title, {
              description: n.message,
              className,
              duration: n.severity === "celebration" ? 8000 : 6000,
              action: n.link ? { label: "Ver", onClick: () => nav(n.link) } : undefined,
            });
            playNotificationAlert(n.severity);
            break; // only one beep per cycle
          }
        }
      }
      lastSeenIdsRef.current = new Set(data.items.map((n) => n.id));
      firstLoadRef.current = false;
    } catch (e) { console.debug("Notifications poll failed:", e); }
  };

  useEffect(() => {
    if (!user) return;
    fetchNow();
    const id = setInterval(fetchNow, POLL_MS);
    // Register web push subscription (best-effort)
    registerPushIfPossible();
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [user?.id]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const openItem = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch (e) { console.debug("Mark read failed:", e); }
    }
    if (n.link) nav(n.link);
    setOpen(false);
    fetchNow();
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); fetchNow(); } catch (e) { console.debug("Mark all read failed:", e); }
  };

  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef} data-testid="notifications-bell-container">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative w-9 h-9 inline-flex items-center justify-center rounded-full border hover:border-[hsl(var(--primary))]/60 transition"
        style={{ borderColor: "hsl(var(--border))" }}
        aria-label="Notificaciones"
        data-testid="notifications-bell-button"
      >
        <Bell size={16} className="text-muted-foreground" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-[hsl(var(--primary))] text-black"
            data-testid="notifications-unread-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 top-12 w-[380px] max-h-[480px] overflow-y-auto rounded-2xl border bg-background shadow-2xl z-50"
          style={{ borderColor: "hsl(var(--border))" }}
          data-testid="notifications-dropdown"
        >
          <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: "hsl(var(--border))" }}>
            <div>
              <div className="serif text-lg">Notificaciones</div>
              {unread > 0 && (
                <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
                  {unread} sin leer
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { playNotificationAlert("high"); }}
                className="text-[10px] text-muted-foreground hover:text-foreground link-underline"
                data-testid="notifications-test-sound"
                title="Probar sonido y vibración"
              >
                🔊 Probar
              </button>
              {unread > 0 && (
                <button onClick={markAll} className="text-xs text-muted-foreground hover:text-foreground link-underline" data-testid="notifications-mark-all">
                  Marcar todas
                </button>
              )}
            </div>
          </div>
          {typeof Notification !== "undefined" && Notification.permission !== "granted" && (
            <button
              onClick={registerPushIfPossible}
              className="w-full text-left p-3 bg-[hsl(var(--primary))]/10 border-b border-[hsl(var(--primary))]/30 hover:bg-[hsl(var(--primary))]/15 transition"
              data-testid="enable-push-banner"
            >
              <div className="text-xs font-medium text-[hsl(var(--primary))]">
                🔔 Activar notificaciones tipo WhatsApp
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                Recibe contrapropuestas y nuevas delegaciones aunque la app esté cerrada
              </div>
            </button>
          )}
          {items.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">
              No tienes notificaciones aún.
            </div>
          ) : (
            <ul className="divide-y" style={{ borderColor: "hsl(var(--border))" }}>
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    onClick={() => openItem(n)}
                    className={`w-full text-left p-4 hover:bg-[hsl(var(--muted))]/40 transition ${
                      !n.read ? "bg-[hsl(var(--muted))]/20" : ""
                    } ${
                      !n.read && n.severity === "high" ? "border-l-2 border-[hsl(var(--primary))]" : ""
                    } ${
                      !n.read && n.severity === "celebration" ? "border-l-2 border-amber-400" : ""
                    }`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="mt-1 shrink-0">{TYPE_ICON[n.type] || <Bell size={14} className="text-muted-foreground"/>}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium leading-snug">{n.title}</div>
                        <div className="text-xs text-muted-foreground mt-1 leading-snug">{n.message}</div>
                        <div className="text-[10px] text-muted-foreground/70 mt-1">
                          {relTime(n.created_at)}
                        </div>
                      </div>
                      {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-[hsl(var(--primary))] shrink-0" />}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function relTime(iso) {
  try {
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return "Hace unos segundos";
    if (s < 3600) return `Hace ${Math.floor(s / 60)} min`;
    if (s < 86400) return `Hace ${Math.floor(s / 3600)} h`;
    return d.toLocaleDateString("es-CL", { day: "numeric", month: "short" });
  } catch { return ""; }
}

// --- Web Push ---------------------------------------------------------------
function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

async function registerPushIfPossible() {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    if (Notification.permission === "denied") return;
    // ask once per session; the prompt is gated by user interaction normally
    if (Notification.permission === "default") {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") return;
    }
    const reg = await navigator.serviceWorker.ready;
    const { default: api } = await import("@/lib/api");
    const { data } = await api.get("/push/public-key");
    const vapid = data?.public_key;
    if (!vapid) return;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid),
      });
    }
    const subJson = sub.toJSON();
    await api.post("/push/subscribe", { endpoint: subJson.endpoint, keys: subJson.keys });
  } catch (err) {
    console.warn("push subscribe failed:", err?.message || err);
  }
}
